// State class for login user information.
import api from '@/common/api';
import config from '../config';
import handler from '../util/apiResultValidator';

export default {
  namespaced: true,

  state: {
    userId: null,
    userName: null,
    group: null,
    oidGroup: null,
    isAgreement: false,
    isAgreementPersonalData: false,
    language: 'en',
    isDelegated: true,
    isAlongToCompany: false,
    companyId: null,
    shopId: null,
    lastPairingNetAppId: null,
    region: null,
    userInfo: {}, // 上記の情報をまとめた情報
    userCompanyInfo: null, // ログインしている会社のユーザー情報
    userCache: {}, // ユーザーに紐づけてキャッシュしたい諸項目,
    signoutConfig: {},
    isTechnicalHq: false,
    functionAvailability: {}, // 機能の利用可否
    isSpecialSupport: false,  // 特殊機能の利用可否
    hasSubscriber: false,
    loginStatus: false,
    searchCache: {}, // 検索条件の保持

  },

  mutations: {

    setUserProperty(state, payload) {
      state.userId = payload.userId;
      state.userName = payload.userName;
      state.group = payload.group;
      state.oidGroup = payload.oidGroup;
      state.isAgreement = payload.isAgreement;
      state.isAgreementPersonalData = payload.isAgreementPersonalData;
      state.language = payload.language;
      state.companyId = payload.companyId;
      state.shopId = payload.shopId;
      state.isDelegated = payload.isDelegated;
      state.lastPairingNetAppId = payload.lastPairingNetAppId;
      state.region = payload.region;
      state.hasSubscriber = payload.hasSubscriber;
      state.isTechnicalHq = payload.isTechnicalHq;
      state.loginStatus = payload.loginStatus;

      // 特殊機能を利用可否のフラグ(利用可否フラグでユーザ情報ではない為、userInfo(全情報)には含めない)
      state.isSpecialSupport = payload.companyId === 'SBR0';

      // 会社ユーザーか店舗ユーザーか判定
      let isAlongToCompany = true;
      if (payload.companyId && payload.shopId) {
        // 両方値があれば 店舗ユーザー
        isAlongToCompany = false;
      }
      state.isAlongToCompany = isAlongToCompany;

      // 全情報を追加する
      state.userInfo = {
        userId: payload.userId,
        userName: payload.userName,
        group: payload.group,
        iodGroup: payload.oidGroup,
        isAgreement: payload.isAgreement,
        isAgreementPersonalData: payload.isAgreementPersonalData,
        language: payload.language,
        isDelegated: payload.isDelegated,
        isAlongToCompany,
        companyId: payload.companyId,
        shopId: payload.shopId,
        lastPairingNetAppId: payload.lastPairingNetAppId,
        region: payload.region,
        isActive: payload.isActive,
        isExternalAuth: payload.isExternalAuth,
        hasSubscriber: payload.hasSubscriber,
        isTechnicalHq: payload.isTechnicalHq,
        loginStatus: payload.login_status
      };
    },

    setUserCompany(state, payload) {
      state.userCompanyInfo = payload;
    },

    setUserCache(state, payload) {
      state.userCache[payload.key] = payload.value;
    },

    initializeCache(state, payload) {
      state.userCache = payload;
    },

    setFunctionAvailability(state, payload) {
      state.functionAvailability = payload;
    },

    setSearchCache(state, payload) {
      state.searchCache[payload.key] = payload.value;
    },

    clearSearchCache(state) {
      state.searchCache[config.CONTENT_TYPE] = 'file';
      state.searchCache[config.INPUT_FILE_NAME] = '';
      state.searchCache[config.INPUT_TARGET_UNIT] = '0' ;
      state.searchCache[config.INPUT_TARGET_PARTS_NUMBER] = '';
      state.searchCache[config.NEW_PARTS_NUMBER] = '';
      state.searchCache[config.INPUT_STATUS] = '';
      state.searchCache[config.INPUT_VEHICLE_MODELS] = '';
      state.searchCache[config.INPUT_CLASSIFICATION] = '';
      state.searchCache[config.INPUT_MODEL_YEAR] = '';
      state.searchCache[config.INPUT_DOMESTIC_APPROVAL] = '';
      state.searchCache[config.INPUT_SUBSCRIBER_APPROVAL] = '';
      state.searchCache[config.INPUT_COMPANY_APPROVAL] = '';
      state.searchCache[config.INPUT_TARGET_IDS] = '';
      state.searchCache[config.INPUT_NEW_IDS] = '';
      state.searchCache[config.INPUT_IS_SU] = '';
      state.searchCache[config.INPUT_REPRO_FILE_TYPE] = '';
      state.searchCache[config.INPUT_PUBLISH_DATE_RANGE_START_DATE] = null;
      state.searchCache[config.INPUT_PUBLISH_DATE_RANGE_END_DATE] = null;
      state.searchCache[config.INPUT_SERVICE_NEWS] = '';
    }
  },

  actions: {

    setUserProperty(context, loginResponse) {
      const userInfo =
      {
        userId: loginResponse.user_id,
        userName: loginResponse.user_name,
        group: loginResponse.group,
        oidGroup: loginResponse.oid_group,
        isAgreement: loginResponse.has_agreement,
        isAgreementPersonalData: loginResponse.has_personal_data_agreement ?? true,
        language: loginResponse.language,
        companyId: loginResponse.company_id,
        shopId: loginResponse.shop_id,
        isDelegated: loginResponse.is_delegated,
        lastPairingNetAppId: loginResponse.last_pairing_net_app_id,
        region: loginResponse.country_group,
        isActive: loginResponse.is_active,
        isExternalAuth: loginResponse.is_external_auth,
        hasSubscriber: loginResponse.has_subscriber,
        isTechnicalHq: loginResponse.is_tech_user,
        loginStatus: loginResponse.login_status
      };
      context.commit('setUserProperty', userInfo);
    },

    setUserCompany(context, payload) {
      context.commit('setUserCompany', payload);
    },

    setUserCache(context, payload) {
      context.commit('setUserCache', payload);
    },

    setSearchCache(context, payload) {
      context.commit('setSearchCache', payload);
    },

    clearSearchCache(context) {
      context.commit('clearSearchCache');
    },


    saveUserCache(context) {
      if (!context.state.userId) return;
      try {
        const params = { caches: JSON.stringify(context.state.userCache) };
        api.postCall(config.ADMIN, '/users/' + context.state.userId + '/caches', params);
      } catch (err) {
        // noop
      }
    },

    async loadUserCache(context) {
      if (!context.state.userId) return;
      const props = await api.getCall(config.ADMIN, '/users/' + context.state.userId + '/caches');
      handler.validate(handler.validateTypes.response, props, null, null, () => {
        try {
          const tmp = JSON.parse(props.data.caches);
          // キャッシュ内容が空、または正常にJSON変換されなかった場合は空オブジェクトで初期化
          if (!tmp || typeof tmp !== 'object')
            context.commit('initializeCache', {});
          else
            context.commit('initializeCache', tmp);
        } catch (_) {
          // 文字列⇒JSON 変換に失敗したらキャッシュ内容を空オブジェクトで初期化
          context.commit('initializeCache', {});
        }
      }, () => {
        // API からキャッシュ内容を正常に取得できなければ空オブジェクトで初期化
        context.commit('initializeCache', {});
      }, null, false);
    },

    setFunctionAvailability(context, payload) {
      const functions =
        [
          config.EXECUE_FUNCTION_TYPE_RPR,
          config.EXECUE_FUNCTION_TYPE_DIAG,
          config.EXECUE_FUNCTION_TYPE_MACKKEY,
          config.EXECUE_FUNCTION_TYPE_IMMOBILIZER,
          config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK,
          config.EXECUE_FUNCTION_TYPE_INDIVIDUAL_DIAG,
          config.EXECUE_FUNCTION_TYPE_REAL_WORLD_DATA,
          config.FUNCTION_TYPE_OBD,
        ];
      const availability = {};
      // 機能一覧の中に取得した機能があれば利用可(true)無ければ利用不可(false)
      payload.forEach(f => {
        availability[f] = functions.includes(f);
      });
      context.commit('setFunctionAvailability', availability);
    }
  },
};